import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock, Mail, Rocket, Shield, TrendingUp, Users } from "lucide-react";
import SpaceJetNavbar from "../components/SpaceJetNavbar";
import SpaceJetParticleField from "../components/SpaceJetParticleField";
import { ThemeProvider } from "../contexts/ThemeContext";
import { authAPI } from "../api";

const SpaceJetNetworkLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginStatus(null);
    setErrorMessage("");

    try {
      const response = await authAPI.login(formData);
      localStorage.setItem("spacejet-user-token", response?.data?.token);
      setLoginStatus("success");
      alert("Login successful! Welcome to SpaceJet Network.");
    } catch (error) {
      console.error("Login failed:", error);

      if (error?.code === "ACCOUNT_PENDING_APPROVAL") {
        navigate("/pending-approval?type=client");
        return;
      }

      setLoginStatus("error");
      setErrorMessage(error.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: "Secure access",
      description: "Enterprise-grade security for your account.",
    },
    {
      icon: TrendingUp,
      title: "Real-time visibility",
      description: "Track your performance with live data and reporting.",
    },
    {
      icon: Users,
      title: "Dedicated support",
      description: "Get help from a team that stays close to your account.",
    },
  ];

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-[#050b17] text-slate-100 spacejet-texture-dots spacejet-texture-living transition-colors duration-300" style={{ "--spacejet-texture-dot-opacity": 0.105, "--spacejet-texture-grid-opacity": 0.07, "--spacejet-texture-ambient-opacity": 0.085, "--spacejet-texture-drift-duration": "50s", "--spacejet-texture-glow-duration": "35s", "--spacejet-texture-sheen-duration": "41s" }}>
        <SpaceJetNavbar />
        <SpaceJetParticleField maxParticles={34} speed={0.12} baseOpacity={0.18} />

        <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-6xl grid-cols-1 items-center gap-12 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <div className="mb-8 inline-flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-500/20 bg-[linear-gradient(180deg,rgba(30,41,59,0.72),rgba(2,6,23,0.84))] shadow-[0_0_24px_rgba(96,165,250,0.08)]">
                <Rocket className="h-6 w-6 text-slate-100" />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.46em] text-slate-400">SpaceJet Network</div>
                <div className="text-2xl font-semibold text-slate-100">Managed login</div>
              </div>
            </div>

            <div className="spacejet-page-kicker mb-4">Sign in</div>
            <h1 className="spacejet-page-title text-4xl font-semibold leading-tight text-slate-100 md:text-5xl">
              Welcome back to your
              <span className="block font-light text-slate-300">SpaceJet account.</span>
            </h1>

            <p className="spacejet-page-copy mt-6 max-w-lg text-xl">
              Access your dashboard, manage campaigns, and track your performance in the network.
            </p>

            <div className="mt-10 space-y-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4">
                    <div className="flex-shrink-0 rounded-lg bg-gradient-to-r from-slate-700 to-slate-800 p-2">
                      <Icon className="h-6 w-6 text-slate-200" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
                      <p className="text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mx-auto w-full max-w-md">
            <div className="spacejet-surface-card rounded-2xl p-8">
              <div className="mb-8 text-center">
                <div className="spacejet-page-kicker mb-3 justify-center">Portal access</div>
                <h2 className="spacejet-page-title text-3xl font-semibold text-slate-100">Sign in</h2>
                <p className="spacejet-page-copy mt-2">Access your SpaceJet Network account</p>
              </div>

              {loginStatus === "error" && (
                <div className="mb-6 rounded-lg border border-red-700/60 bg-red-900/40 p-4 text-red-200">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-300">Email address</label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute inset-y-0 left-0 ml-3 flex h-5 w-5 items-center text-slate-400" />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Enter your email" />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-300">Password</label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute inset-y-0 left-0 ml-3 flex h-5 w-5 items-center text-slate-400" />
                    <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleInputChange} required className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-12 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Enter your password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {showPassword ? <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-300" /> : <Eye className="h-5 w-5 text-slate-400 hover:text-slate-300" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 rounded border-slate-600 bg-slate-700 text-slate-600 focus:ring-slate-500" />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-300">Remember me</label>
                  </div>
                  <Link to="/contact" className="text-sm font-medium text-slate-300 transition-colors hover:text-slate-100">
                    Forgot password?
                  </Link>
                </div>

                <button type="submit" disabled={isLoading} className="spacejet-btn-primary group inline-flex w-full items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60">
                  {isLoading ? "Signing in..." : "Sign in"}
                  <ArrowRight className={`ml-2 h-5 w-5 transition-transform ${isLoading ? "animate-pulse" : "group-hover:translate-x-1"}`} />
                </button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-slate-400">
                  Don&apos;t have an account?{" "}
                  <Link to="/signup" className="font-medium text-slate-300 transition-colors hover:text-slate-100">
                    Sign up here
                  </Link>
                </p>
              </div>

              <div className="mt-6 border-t border-slate-700 pt-6 text-center text-sm text-slate-400">
                Login for the network happens in LinkTrust. This page remains available for redirects and account flow.
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="inline-flex items-center text-slate-400 transition-colors hover:text-slate-300">
                ← Back to SpaceJet Network
              </Link>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SpaceJetNetworkLogin;
